import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api";
import CardPaginator from "./CardPaginator";
import LearnModeParameterized from "./LearnModeParameterized";
import { jwtDecode } from "jwt-decode";

const BATCH_SIZE = 5;

const LearningManager = ({ token }) => {
    let { deckId } = useParams();
    const navigate = useNavigate();

    const [deck, setDeck] = useState(null);
    const [cards, setCards] = useState([]);
    const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
    const [reviewCompleted, setReviewCompleted] = useState(false);
    const [learningCompleted, setLearningCompleted] = useState(false);
    const [userId, setUserId] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState(null);

    const learningSubmitted = useRef(false);


    useEffect(() => {
        const fetchCards = async () => {
            try {
                const deckResponse = await api.get(`/decks/${deckId}`);
                setDeck(deckResponse.data.deck);
                console.log(deck)

                const cardsResponse = await api.get(`/decks/${deckId}/learn`);
                setCards(cardsResponse.data.newCards || []);

                const decoded = jwtDecode(token);
                setUserId(decoded.user._id);
            } catch (error) {
                console.error("Error fetching data:", error);
                setCards([]);
            }
        };

        fetchCards();
    }, [deckId, token]);

    const getCurrentBatch = () => {
        const start = currentBatchIndex * BATCH_SIZE;
        return cards.slice(start, start + BATCH_SIZE);
    };

    const handleReviewComplete = () => {
        setReviewCompleted(true);
    };


    const handleLearningComplete = async () => {
        if (learningSubmitted.current) {
            // console.log("handleLearningComplete already executed, skipping...");
            return;
        }
        learningSubmitted.current = true; // Mark as executed

        // console.log("handleLearningComplete triggered");

        const batchedCards = getCurrentBatch();

        try {
            await Promise.all(
                batchedCards.map(card =>
                    api.post(`/cards/submit-answer`, {
                        userId,
                        cardId: card._id,
                        deckId: deck._id,
                        incorrectAttempts: 0, // Keep as is
                    }).catch(err => console.error("Error submitting answer:", err))
                )
            );

            console.log("Completed API calls to submission");
            setLearningCompleted(true);
        } catch (error) {
            console.error("Error in handleLearningComplete:", error);
        }
    };


    useEffect(() => {
        if (reviewCompleted && learningCompleted) {
            const nextBatchIndex = currentBatchIndex + 1;
            if (nextBatchIndex * BATCH_SIZE < cards.length) {
                setCurrentBatchIndex(nextBatchIndex);
                setReviewCompleted(false);
                setLearningCompleted(false);
            } else {
                setLoadingMessage("No more learning items present, redirecting you to dashboard.");
                setTimeout(() => navigate("/dashboard"), 1000); // 1-second delay
            }
        }
    }, [reviewCompleted, learningCompleted, currentBatchIndex, cards.length, navigate]);

    if (!deck || !userId) {
        return <p>Loading...</p>;
    }

    if (cards.length === 0) {
        return (
            <div>
                <p>Loading...</p>
                {loadingMessage && <p>{loadingMessage}</p>}
            </div>
        );
    }

    const batchedCards = getCurrentBatch();


    return (
        <div>
            {loadingMessage ? (
                <p>{loadingMessage}</p>
            ) : !reviewCompleted ? (
                <CardPaginator
                    cards={batchedCards}
                    deck={deck}
                    userId={userId}
                    onReviewComplete={handleReviewComplete}
                />
            ) : !learningCompleted ? (
                <LearnModeParameterized
                    token={token}
                    cards={batchedCards}
                    deck={deck}
                    onComplete={handleLearningComplete}
                />
            ) : (
                <p>Loading next batch...</p>
            )}
        </div>
    );
};

export default LearningManager;
